import { Classes, Intent, Label } from '@blueprintjs/core';
import * as React from 'react';
import { alphaSort, isEqual } from 'vet-bones/utils';

import { EditableLabel } from 'src/components/EditableLabel';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useValidateSensitivePropertyGroupName } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ProjectDiagramCreateEntityDrawer } from 'src/containers/ProjectDiagram/ProjectDiagramCreateEntityDrawer';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addAttribute';
import { addSensitivePropertyGroup } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/addSensitivePropertyGroup';
import { projectAttributeItemsSelector } from 'src/selectors/projectAttributeItems';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramCreateSensitivePropertyGroupDrawer: React.VFC =
  () => {
    const { projectId } = useRouterParams();
    const dispatch = useReduxDispatch();
    const diagram = useDiagram();

    const { isOpen, attributeIri } = useReduxSelector(
      (state) => state.ui.createSensitivePropertyGroupDrawer
    );
    const didIsOpenChange = useDidValueChange(isOpen);

    const [errorText, setErrorText] = React.useState(
      copy.components.projectDiagram.editableIri.validationLabelRequired
    );
    const [name, setName] = React.useState('');
    const [didEditName, setDidEditName] = React.useState(false);

    const allAttributeItems = useReduxSelector(
      projectAttributeItemsSelector(projectId),
      isEqual
    );

    const targetModelId = useReduxSelector(
      projectTargetModelIdSelector(projectId)
    );

    const [attributes, setAttributes] = React.useState<AttributeTableItem[]>(
      allAttributeItems.filter((item) => item.value === attributeIri)
    );

    React.useEffect(() => {
      if (didIsOpenChange && isOpen) {
        setAttributes(
          allAttributeItems.filter((item) => item.value === attributeIri)
        );
      }
    }, [allAttributeItems, attributeIri, isOpen, didIsOpenChange]);

    const validateSensitivePropertyGroupName =
      useValidateSensitivePropertyGroupName();

    const validateName = (name: string) => {
      if (!didEditName) {
        return '';
      }

      const trimmedName = name.trim();
      return validateSensitivePropertyGroupName(trimmedName);
    };

    const handleUpdateName = (name: string) => {
      const trimmedName = name.trim();
      setName(trimmedName);
      setDidEditName(true);
      const nameError = validateName(name);
      setErrorText(nameError);
    };

    const handleAddAttribute = (attribute: AttributeTableItem) => {
      if (!attribute) {
        return;
      }
      setAttributes(
        [...attributes, attribute].sort(alphaSort((item) => item.text))
      );
    };

    const handleRemoveAttribute = (attribute: AttributeTableItem) => {
      if (!attribute) {
        return;
      }
      setAttributes(
        attributes.filter((item) => item.value !== attribute.value)
      );
    };

    const handleCancel = () => {
      setErrorText(
        copy.components.projectDiagram.sensitivePropertyGroupDrawer.validation
          .name.required
      );
      setName('');
      setDidEditName(false);
      setAttributes([]);
      dispatch(
        uiSlice.actions.toggledCreateSensitivePropertyGroupDrawer({
          isOpen: false,
        })
      );
    };

    const handleSubmit = () => {
      if (errorText || !diagram) {
        return;
      }

      commitDiagramModel(
        diagram,
        (model) => {
          attributes.forEach((attribute) => {
            if (attribute.isNewlyCreated && !attribute.text) {
              return;
            }

            if (attribute.isNewlyCreated) {
              addAttribute({
                ...attribute,
                projectModelIds: targetModelId ? [targetModelId] : [],
              })(model);
            }
          });

          addSensitivePropertyGroup(
            name,
            attributes.map((attribute) => attribute.value)
          )(model);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );

      handleCancel();
    };

    return (
      <ProjectDiagramCreateEntityDrawer
        disabled={Boolean(errorText)}
        errorIntent={didEditName ? Intent.DANGER : Intent.NONE}
        errorText={errorText}
        isOpen={isOpen}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        title={
          copy.components.projectDiagram.sensitivePropertyGroupDrawer
            .createTitle
        }
      >
        <Label className="sd-drawer-editable-label">
          {copy.components.projectDiagram.sensitivePropertyGroupDrawer.name}
          <div className={Classes.CALLOUT}>
            <EditableLabel
              onSetValue={handleUpdateName}
              onValidate={validateName}
              placeholder={
                name === ''
                  ? copy.components.projectDiagram.sensitivePropertyGroupDrawer
                      .new
                  : ''
              }
              setValueOnChange
              value={name}
            />
          </div>
        </Label>
        <ProjectDiagramAttributesTable
          attributes={attributes}
          onAddAttribute={handleAddAttribute}
          onUnlinkAttribute={handleRemoveAttribute}
        />
      </ProjectDiagramCreateEntityDrawer>
    );
  };
